import React, { useState, useEffect } from "react";
import Collapse from "react-bootstrap/Collapse";
import Swal from "sweetalert2";
import Select from "react-select";
import ProductServices from "../../../services/ProductServices";
import RepairServies from "../../../services/RepairServices";

const NewRepairRequest = ({ onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const user = JSON.parse(localStorage.getItem("userDetails"));
  const availableStores = user?.data?.user?.store || [];
  const defaultStore = availableStores[0]?._id || "";

  const [stores] = useState(availableStores);
  const [selectedStore, setSelectedStore] = useState(defaultStore);

  const [fixedInventory, setFixedInventory] = useState(null);

  // FUNCTION TO CREATE A FRESH EMPTY ROW
  const createEmptyRow = () => ({
    name: "",
    inventory: "",
    masterlist: "",
    description: "",        // inventory description
    masterDescription: "",  // 👈 NEW
    currentQty: 0,
    auth: 0,
    qtyRequired: "",
    unit: "",
    partNo: "",
    error: "",
  });


  const [items, setItems] = useState([createEmptyRow()]);

  const [inventoryOptions, setInventoryOptions] = useState([]);
  const [masterlistOptions, setMasterlistOptions] = useState([]);

  // ========================================================
  // LOAD MASTERLIST
  // ========================================================
  useEffect(() => {
    const fetchMasterlist = async () => {
      try {
        const res = await ProductServices.getAllProduct();

        const options = res?.data?.data?.map((item) => ({
          value: item._id,
          label: `${item.partNo} | ${item.description}`,
          unit: item.unit?.name,
          partNo: item.partNo,
          searchKey: `${item.partNo} ${item.description}`.toLowerCase(),
        }));

        setMasterlistOptions(options);
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Masterlist Load Failed",
          text: "Unable to load masterlist data. Please try again later.",
        });
      }
    };

    fetchMasterlist();
  }, []);

  // SEARCH FILTER
  const filterMasterlist = (option, input) =>
    !input || option.data.searchKey.includes(input.toLowerCase());

  // ========================================================
  // LOAD INVENTORY WHEN STORE CHANGES
  // ========================================================
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const res = await ProductServices.getInventory(selectedStore);
        const options = res.data.data.map((part) => ({
          value: part._id,
          label: `${part?.masterItem?.partNo} | ${part?.masterItem?.description}`,
          description: part?.masterItem?.description,
          currentStock: part?.currentStock,
          auth: part?.qtyAuth,
          unit: part?.masterItem?.unit?.name,
        }));

        setInventoryOptions(options);
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Inventory Load Failed",
          text: "Unable to load inventory for this store.",
        });
      }
    };

    fetchInventory();
    setFixedInventory(null);
    setItems([createEmptyRow()]);
  }, [selectedStore]);

  // ========================================================
  // SELECT INVENTORY
  const handlePartSelect = (index, option) => {
    if (fixedInventory && index !== 0) return;

    const updated = [...items];

    updated[index] = {
      ...updated[index],
      inventory: option.value,
      name: option.label,
      description: option.description,           // Inventory description
      masterDescription: option.description,     // 🔥 Master description
      currentQty: option.currentStock,
      auth: option.auth,
      unit: option.unit,
    };

    if (!fixedInventory) setFixedInventory(option);

    setItems(updated);
  };


  // ========================================================
  // SELECT MASTERLIST (prevent duplicates)
  const handleMasterlistSelect = (index, option) => {
    const duplicate = items.some(
      (it, i) => it.masterlist === option.value && i !== index
    );

    if (duplicate) {
      Swal.fire({
        icon: "error",
        title: "Duplicate Masterlist",
        text: "This masterlist is already selected!",
      });
      return;
    }

    const updated = [...items];

    updated[index] = {
      ...updated[index],
      masterlist: option.value,
      partNo: option.partNo,
      masterDescription: option.label,   // ✅ FIXED: show master description
      masterUnit: option.unit            // optional
    };

    setItems(updated);
  };

  // TEXT INPUT CHANGE
  const handleItemChange = (index, field, val) => {
    const updated = [...items];
    updated[index][field] = val;
    if (field === "qtyRequired") updated[index].error = "";
    setItems(updated);
  };

  // ========================================================
  // ADD ROW
  const handleAddItem = (e) => {
    e.preventDefault();

    const last = items[items.length - 1];

    // 🚫 Block if inventory not selected
    if (!last.inventory) {
      Swal.fire({
        icon: "warning",
        title: "Inventory Required",
        text: "Please select inventory before adding a new row.",
      });
      return;
    }

    // 🚫 Block if repair part not selected
    if (!last.masterlist) {
      Swal.fire({
        icon: "warning",
        title: "Repair Part Required",
        text: "Please select repair part before adding a new row.",
      });
      return;
    }

    // 🚫 Block invalid qty
    if (!last.qtyRequired || last.qtyRequired <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Quantity",
        text: "Please enter a valid repair quantity.",
      });
      return;
    }

    // ✅ ADD NEW ROW
    const newRow = {
      ...createEmptyRow(),
      inventory: fixedInventory?.value || "",
      name: fixedInventory?.label || "",
      description: fixedInventory?.description || "",
      currentQty: fixedInventory?.currentStock || 0,
      auth: fixedInventory?.auth || 0,
    };

    setItems([...items, newRow]);
  };


  // REMOVE ROW
  const handleRemoveItem = (e, index) => {
    e.preventDefault();
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated.length ? updated : [createEmptyRow()]);
  };

  // ========================================================
  // SUBMIT REQUEST
  // ========================================================
  const handleSubmit = (e) => {
    e.preventDefault();

    // ❗ FULL MASTERLIST DUPLICATE CHECK BEFORE SUBMITTING
    const masterlistIds = items.map((it) => it.masterlist);
    const duplicates = masterlistIds.filter(
      (id, idx) => masterlistIds.indexOf(id) !== idx
    );

    if (duplicates.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Duplicate Masterlist Detected",
        text: "Each Masterlist item must be unique.",
      });
      return;
    }

    // Qty validation
    for (const item of items) {
      if (!item.qtyRequired || item.qtyRequired <= 0) {
        Swal.fire({
          icon: "error",
          title: "Qty Error",
          text: "Repair qty cannot be blank or zero!",
        });
        return;
      }
    }

    setIsSubmitting(true);

    const requestData = {
      store: selectedStore,
      items,
    };

    RepairServies.newSubmitRepairRequest(requestData)
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Repair Request Submitted",
          text: "Your repair request has been created.",
        });

        // RESET FORM PROPERLY
        setItems([createEmptyRow()]);
        setFixedInventory(null);
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "Submit Failed",
          text:
            err?.response?.data?.message ||
            "Something went wrong while submitting.",
        });
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className="row">
      <div className="col-xl-12">

        {/* STORE FILTER */}
        <div className="filter cm-content-box box-primary">
          <div className="content-title">
            <div className="cpa">
              <i className="fas fa-filter me-2"></i>Filter
            </div>
          </div>
          <div className="card-body">
            <label>Select Store</label>
            <select
              className="form-select"
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
            >
              {stores.map((store) => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ADD ITEMS */}
        <div className="filter cm-content-box box-primary">
          <div className="content-title">
            <div className="cpa">
              <i className="fa fa-plus me-2"></i>Add Repair Item
            </div>
          </div>

          <div className="card-body">
            <form>
              {items.map((item, index) => (
                <div className="row" key={index}>

                  {/* INVENTORY SELECT */}
                  <div className="col-md-2 mb-2">
                    <label>Select Inventory</label>
                    <Select
                      options={inventoryOptions}
                      value={
                        item.inventory
                          ? inventoryOptions.find((i) => i.value === item.inventory)
                          : null
                      }
                      onChange={(opt) => handlePartSelect(index, opt)}
                      isDisabled={index !== 0 && fixedInventory}
                      placeholder="Select Inventory"
                    />
                  </div>

                  {/* MASTERLIST SELECT */}
                  <div className="col-md-2 mb-2">
                    <label>Select Reapir Part</label>
                    <Select
                      options={masterlistOptions}
                      value={
                        item.masterlist
                          ? masterlistOptions.find((m) => m.value === item.masterlist)
                          : null
                      }
                      onChange={(opt) => handleMasterlistSelect(index, opt)}
                      filterOption={filterMasterlist}
                      placeholder="Search PartNo / Description"
                    />
                  </div>

                  {/* DESCRIPTION */}
                  <div className="col-md-2 mb-2">
                    <label>Inventory Description</label>
                    <input
                      className="form-control form-control-sm"
                      readOnly
                      value={item.description}
                    />
                  </div>
                  {/* MASTER DESCRIPTION */}
                  <div className="col-md-2 mb-2">
                    <label>Repai Part Description</label>
                    <input
                      className="form-control form-control-sm"
                      readOnly
                      value={item.masterDescription}
                    />
                  </div>

                  {/* AUTH 
                  <div className="col-md-1 mb-2">
                    <label>Auth</label>
                    <input
                      className="form-control form-control-sm"
                      readOnly
                      value={item.auth}
                    />
                  </div>
                  */}

                  {/* CURRENT QTY */}
                  <div className="col-md-2 mb-2">
                    <label>Inv. Current Qty</label>
                    <input
                      className="form-control form-control-sm"
                      readOnly
                      value={item.currentQty}
                    />
                  </div>

                  {/* REQUIRED QTY */}
                  <div className="col-md-2 mb-2">
                    <label>Qty</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.qtyRequired}
                      onChange={(e) =>
                        handleItemChange(index, "qtyRequired", e.target.value)
                      }
                    />
                  </div>

                  {/* DELETE */}
                  <div className="col-md-1 mb-2">
                    <button
                      className="btn btn-danger btn-xs sharp mt-4"
                      onClick={(e) => handleRemoveItem(e, index)}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>

                </div>
              ))}

              {/* BUTTONS */}
              <div className="row mt-2">
                <div className="col-md-2">
                  <button className="btn btn-primary" onClick={handleAddItem}>
                    Add
                  </button>
                </div>

                <div className="col-md-2">
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </div>

            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default NewRepairRequest;
